import { ChangeEvent, createElement, FocusEvent, MutableRefObject, ReactElement, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyGhgConflictDialog } from "@/domain/municipal-property/components/municipal-property-ghg-conflict-dialog";
import { useMunicipalPropertyYearMutations } from "@/domain/municipal-property/hooks/api/use-municipal-property-year-mutations";
import { isMunicipalPropertyConsumptionConflict } from "@/domain/municipal-property/lib/is-municipal-property-consumption-conflict";
import { MunicipalPropertyGhgConflictPreview, readMunicipalPropertyGhgConflict } from "@/domain/municipal-property/lib/read-municipal-property-ghg-conflict";
import { getConsumptionCellKey } from "@/domain/municipal-property/models/get-consumption-cell-key";
import {
  BatchUpdateMunicipalPropertyValueInput,
  BatchUpdateMunicipalPropertyValueInput_ValueList_SectorKeyEnum_0,
  MunicipalPropertyYearQuery,
} from "@/graphql/generated/graphql";
import { fromJoules, isGhgQuality, MUNICIPAL_PROPERTY_VALUE_CELL_POLICY, toGhgQuality } from "@ark/shared";

type ValueCell = (typeof MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.cellList)[number];
type StoredValue = MunicipalPropertyYearQuery["municipalPropertyYear"]["valueList"][number];
type InputValue = BatchUpdateMunicipalPropertyValueInput["valueList"][number];
interface FieldSnapshot {
  amount: string;
  dataSource: string;
  gpcQualityRating: string;
}
const SNAPSHOT_FIELD_MAP = { amount: "amount", source: "dataSource", quality: "gpcQualityRating" } as const;

const cancelledQueueVersionMap = new Map<string, number>();

const saveQueueMap = new Map<string, { promise: Promise<void>; mountId: symbol }>();

const API_SECTOR_KEY_MAP: Record<ValueCell["sectorKey"], BatchUpdateMunicipalPropertyValueInput_ValueList_SectorKeyEnum_0> = {
  MUNICIPAL_FACILITIES: BatchUpdateMunicipalPropertyValueInput_ValueList_SectorKeyEnum_0.MunicipalFacilities,
  WATER_CONSUMPTION: BatchUpdateMunicipalPropertyValueInput_ValueList_SectorKeyEnum_0.WaterConsumption,
};

interface AutosaveOptions {
  year: MunicipalPropertyYearQuery["municipalPropertyYear"];
  canWrite: boolean;
  mutation: ReturnType<typeof useMunicipalPropertyYearMutations>;
  cellList: readonly ValueCell[];
}
export interface MunicipalPropertyConsumptionAutosave {
  conflictDialog: ReactElement | null;
  formRef: MutableRefObject<HTMLFormElement | null>;
  handleChange: (event: ChangeEvent<HTMLFormElement>) => void;
  handleBlur: (event: FocusEvent<HTMLFormElement>) => void;
  draftMap: ReadonlyMap<string, FieldSnapshot>;
  handleCellUpdate: (cell: { rowId: string; columnId: string; value: string | number | null }) => void;
}

export function useMunicipalPropertyConsumptionAutosave({
  year,
  canWrite,
  mutation,
  cellList,
}: AutosaveOptions): MunicipalPropertyConsumptionAutosave {
  const { t } = useDomainTranslation("municipal-property");
  const mountId = useRef(Symbol());
  const isMounted = useRef(true);
  const pendingConfirmation = useRef<((confirmed: boolean) => void) | null>(null);
  const [conflictPreview, setConflictPreview] = useState<MunicipalPropertyGhgConflictPreview | null>(null);
  useLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      pendingConfirmation.current?.(false);
    };
  }, []);

  function settleConfirmation({ isConfirmed }: { isConfirmed: boolean }): void {
    pendingConfirmation.current?.(isConfirmed);
    pendingConfirmation.current = null;
    setConflictPreview(null);
  }

  async function saveWithConfirmation(input: BatchUpdateMunicipalPropertyValueInput): Promise<boolean> {
    let confirmation: string | undefined;
    for (;;) {
      try {
        await mutation.updateValueList.mutateAsync({
          ...input,
          ...(confirmation ? { overwriteConfirmation: confirmation } : {}),
        });
        return true;
      } catch (error) {
        const preview = readMunicipalPropertyGhgConflict(error);
        if (!preview) throw error;
        if (!isMounted.current) {
          toast.error(t("register.property.conflictError"));
          return false;
        }
        setConflictPreview(preview);
        const isConfirmed = await new Promise<boolean>((resolve) => {
          pendingConfirmation.current = resolve;
        });
        if (!isConfirmed) {
          if (!isMounted.current) toast.error(t("register.property.conflictError"));
          return false;
        }
        confirmation = preview.fingerprint;
      }
    }
  }
  const draftFieldSet = useRef(new Set<string>());
  const [draftMap, setDraftMap] = useState<ReadonlyMap<string, FieldSnapshot>>(new Map());
  const gridDraftMap = useRef(new Map<string, FieldSnapshot>());
  const savedFieldMap = useRef(new Map<string, FieldSnapshot>());
  const formRef: MutableRefObject<HTMLFormElement | null> = useRef<HTMLFormElement | null>(null);
  const serverFieldMap: MutableRefObject<Map<string, FieldSnapshot>> = useRef(new Map<string, FieldSnapshot>());
  const pendingCountMap: MutableRefObject<Map<string, number>> = useRef(new Map<string, number>());
  useEffect((): void => {
    const form: HTMLFormElement | null = formRef.current;
    let hasGridChanges = false;
    for (const cell of cellList) {
      const key: string = getConsumptionCellKey(cell);
      const savedKey: string = `${year.id}:${key}`;
      const value: StoredValue | undefined = year.valueList.find((item: StoredValue): boolean => getConsumptionCellKey(item) === key);
      const snapshot: FieldSnapshot = {
        amount: value ? String(cell.storageUnit === "J" ? fromJoules(value.amount, "MWh") : value.amount) : "",
        dataSource: value?.dataSource ?? "",
        gpcQualityRating: value?.gpcQualityRating ?? "",
      };
      const previous: FieldSnapshot | undefined = serverFieldMap.current.get(savedKey);
      const saved: FieldSnapshot = savedFieldMap.current.get(savedKey) ?? snapshot;
      const amountInput = form?.elements.namedItem(`amount:${key}`);
      const sourceInput = form?.elements.namedItem(`source:${key}`);
      const hasAmountDraft: boolean = draftFieldSet.current.has(`${savedKey}:amount`);
      const hasSourceDraft: boolean = draftFieldSet.current.has(`${savedKey}:source`);
      const hasQualityDraft = draftFieldSet.current.has(`${savedKey}:quality`);
      const gridDraft = gridDraftMap.current.get(savedKey);
      if (gridDraft) {
        const next = {
          amount: hasAmountDraft || pendingCountMap.current.get(`${savedKey}:amount`) ? gridDraft.amount : snapshot.amount,
          dataSource: hasSourceDraft || pendingCountMap.current.get(`${savedKey}:source`) ? gridDraft.dataSource : snapshot.dataSource,
          gpcQualityRating: hasQualityDraft || pendingCountMap.current.get(`${savedKey}:quality`) ? gridDraft.gpcQualityRating : snapshot.gpcQualityRating,
        };
        if (next.amount !== gridDraft.amount || next.dataSource !== gridDraft.dataSource || next.gpcQualityRating !== gridDraft.gpcQualityRating) {
          gridDraftMap.current.set(savedKey, next);
          hasGridChanges = true;
        }
      }
      if (
        previous &&
        !pendingCountMap.current.get(`${savedKey}:amount`) &&
        amountInput instanceof HTMLInputElement &&
        !hasAmountDraft
      )
        amountInput.value = snapshot.amount;
      if (
        previous &&
        !pendingCountMap.current.get(`${savedKey}:source`) &&
        sourceInput instanceof HTMLInputElement &&
        !hasSourceDraft
      )
        sourceInput.value = snapshot.dataSource;
      serverFieldMap.current.set(savedKey, snapshot);
      savedFieldMap.current.set(savedKey, {
        amount: pendingCountMap.current.get(`${savedKey}:amount`) || hasAmountDraft ? saved.amount : snapshot.amount,
        dataSource: pendingCountMap.current.get(`${savedKey}:source`) || hasSourceDraft ? saved.dataSource : snapshot.dataSource,
        gpcQualityRating: pendingCountMap.current.get(`${savedKey}:quality`) || hasQualityDraft ? saved.gpcQualityRating : snapshot.gpcQualityRating,
      });
    }
    if (hasGridChanges) setDraftMap(new Map(gridDraftMap.current));
  }, [year.id, year.valueList, cellList]);

  function handleChange(event: ChangeEvent<HTMLFormElement>): void {
    if (!(event.target instanceof HTMLInputElement)) return;
    const [field, ...coordinatePartList]: string[] = event.target.name.split(":");
    draftFieldSet.current.add(`${year.id}:${coordinatePartList.join(":")}:${field}`);
  }

  function synchronizeSavedField({
    savedKey,
    field,
    input,
    expectedValue,
    snapshot,
  }: {
    savedKey: string;
    field: "amount" | "source" | "quality";
    input?: HTMLInputElement;
    expectedValue: string;
    snapshot: FieldSnapshot;
  }): void {
    const draftKey: string = `${savedKey}:${field}`;
    const currentValue = input
      ? field === "amount" ? input.value : input.value.trim()
      : gridDraftMap.current.get(savedKey)?.[SNAPSHOT_FIELD_MAP[field]];
    if (currentValue !== expectedValue || (pendingCountMap.current.get(draftKey) ?? 0) > 0) return;
    const snapshotField = SNAPSHOT_FIELD_MAP[field];
    if (input) input.value = snapshot[snapshotField];
    draftFieldSet.current.delete(draftKey);
    savedFieldMap.current.set(savedKey, {
      ...(savedFieldMap.current.get(savedKey) ?? snapshot),
      [snapshotField]: snapshot[snapshotField],
    });
    if (!input) {
      const draft = gridDraftMap.current.get(savedKey) ?? snapshot;
      gridDraftMap.current.set(savedKey, { ...draft, [snapshotField]: snapshot[snapshotField] });
      setDraftMap(new Map(gridDraftMap.current));
    }
  }

  function saveField({ key, amount, dataSource, gpcQualityRating, isAmountField, isQualityField = false, amountInput, sourceInput }: {
    key: string;
    amount: string;
    dataSource: string;
    gpcQualityRating: string;
    isAmountField: boolean;
    isQualityField?: boolean;
    amountInput?: HTMLInputElement;
    sourceInput?: HTMLInputElement;
  }): void {
    if (!canWrite) return;
    const cell: ValueCell | undefined = cellList.find((item: ValueCell): boolean => getConsumptionCellKey(item) === key);
    if (!cell) return;
    if (amount !== "" && (!Number.isFinite(Number(amount)) || Number(amount) < 0)) return;
    if (dataSource.length > MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.dataSourceMaxLength) return;
    const isCleared: boolean = amount === "";
    const savedKey: string = `${year.id}:${key}`;
    const isClearingAmount: boolean = isAmountField && isCleared &&
      (savedFieldMap.current.get(savedKey)?.amount ?? amountInput?.defaultValue ?? "") !== "";
    if (isCleared && dataSource !== "" && !isClearingAmount) {
      toast.error(t("register.property.dataSourceRequiresAmount"));
      return;
    }
    if (isCleared && isQualityField && gpcQualityRating !== "") {
      toast.error(t("register.property.qualityRequiresAmount"));
      return;
    }
    const shouldSaveDataSource = (!isAmountField && !isQualityField) || (!isCleared && isAmountField && draftFieldSet.current.has(`${savedKey}:source`));
    const shouldSaveQuality = isQualityField || (!isCleared && isAmountField && draftFieldSet.current.has(`${savedKey}:quality`));
    const pendingKeyList = [
      ...(isAmountField ? [`${savedKey}:amount`] : []),
      ...(shouldSaveDataSource || (isAmountField && isCleared) ? [`${savedKey}:source`] : []),
      ...(shouldSaveQuality || (isAmountField && isCleared) ? [`${savedKey}:quality`] : []),
    ];
    for (const pendingKey of pendingKeyList)
      pendingCountMap.current.set(pendingKey, (pendingCountMap.current.get(pendingKey) ?? 0) + 1);
    const queueVersion = cancelledQueueVersionMap.get(year.id) ?? 0;
    const previousQueue = saveQueueMap.get(year.id);
    const hasOtherMountSave = !!previousQueue && previousQueue.mountId !== mountId.current;
    const saveQueue = (previousQueue?.promise ?? Promise.resolve()).then(async (): Promise<void> => {
      let completedSnapshot: FieldSnapshot | undefined;
      try {
        if ((cancelledQueueVersionMap.get(year.id) ?? 0) !== queueVersion) return;
        const previous: FieldSnapshot = savedFieldMap.current.get(savedKey) ?? { amount: "", dataSource: "", gpcQualityRating: "" };
        const isAmountChanged: boolean =
          isAmountField &&
          ((hasOtherMountSave && draftFieldSet.current.has(`${savedKey}:amount`)) ||
            (amount === "" ? null : Number(amount)) !== (previous.amount === "" ? null : Number(previous.amount)));
        const isDataSourceChanged: boolean = shouldSaveDataSource &&
          ((hasOtherMountSave && draftFieldSet.current.has(`${savedKey}:source`)) || dataSource !== previous.dataSource);
        const isQualityChanged = shouldSaveQuality && ((hasOtherMountSave && draftFieldSet.current.has(`${savedKey}:quality`)) || gpcQualityRating !== previous.gpcQualityRating);
        if (!isAmountChanged && !isDataSourceChanged && !isQualityChanged) {
          completedSnapshot = serverFieldMap.current.get(savedKey) ?? previous;
          return;
        }
        const input: InputValue = {
          sectorKey: API_SECTOR_KEY_MAP[cell.sectorKey],
          subsectorKey: cell.subsectorKey ?? null,
          energySourceKey: cell.energySourceKey,
          ...(isAmountChanged ? { amount: isCleared ? null : Number(amount) } : {}),
          ...(isQualityChanged ? { gpcQualityRating: toGhgQuality(gpcQualityRating) } : {}),
          ...(isAmountChanged && isCleared
            ? { dataSource: null }
            : isDataSourceChanged
              ? { dataSource: dataSource || null }
              : {}),
        };
        const serverBeforeSave: FieldSnapshot | undefined = serverFieldMap.current.get(savedKey);
        const isSaved = await saveWithConfirmation({ propertyYearId: year.id, valueList: [input] });
        if (!isSaved) {
          if (isMounted.current) cancelledQueueVersionMap.set(year.id, queueVersion + 1);
          return;
        }
        const current: FieldSnapshot = savedFieldMap.current.get(savedKey) ?? previous;
        const saved: FieldSnapshot = {
          amount: isAmountChanged ? amount : current.amount,
          dataSource: isAmountChanged && isCleared ? "" : isDataSourceChanged ? dataSource : current.dataSource,
          gpcQualityRating: isAmountChanged && isCleared ? "" : isQualityChanged ? gpcQualityRating : current.gpcQualityRating,
        };
        const serverAfterSave: FieldSnapshot | undefined = serverFieldMap.current.get(savedKey);
        const latest: FieldSnapshot = serverAfterSave &&
          (serverAfterSave.amount !== serverBeforeSave?.amount || serverAfterSave.dataSource !== serverBeforeSave?.dataSource || serverAfterSave.gpcQualityRating !== serverBeforeSave?.gpcQualityRating)
            ? serverAfterSave
            : saved;
        savedFieldMap.current.set(savedKey, saved);
        if (latest === saved) serverFieldMap.current.set(savedKey, saved);
        completedSnapshot = latest;
      } catch (error) {
        toast.error(
          t(
            isMunicipalPropertyConsumptionConflict(error)
              ? "register.property.conflictError"
              : "register.property.saveError",
          ),
        );
      } finally {
        for (const pendingKey of pendingKeyList)
          pendingCountMap.current.set(pendingKey, Math.max(0, (pendingCountMap.current.get(pendingKey) ?? 1) - 1));
        if (completedSnapshot) {
          if (pendingKeyList.includes(`${savedKey}:amount`))
            synchronizeSavedField({ savedKey, field: "amount", input: amountInput, expectedValue: amount, snapshot: completedSnapshot });
          if (pendingKeyList.includes(`${savedKey}:source`))
            synchronizeSavedField({ savedKey, field: "source", input: sourceInput, expectedValue: dataSource, snapshot: completedSnapshot });
          if (pendingKeyList.includes(`${savedKey}:quality`))
            synchronizeSavedField({ savedKey, field: "quality", expectedValue: gpcQualityRating, snapshot: completedSnapshot });
        }
      }
    });
    saveQueueMap.set(year.id, { promise: saveQueue, mountId: mountId.current });
    void saveQueue.then(() => {
      if (saveQueueMap.get(year.id)?.promise === saveQueue) {
        saveQueueMap.delete(year.id);
        cancelledQueueVersionMap.delete(year.id);
      }
    });
  }

  function saveInput({ form, input }: { form: HTMLFormElement; input: HTMLInputElement }): void {
    const key = input.name.replace(/^(amount|source):/, "");
    const amountInput = form.elements.namedItem(`amount:${key}`);
    const sourceInput = form.elements.namedItem(`source:${key}`);
    if (!(amountInput instanceof HTMLInputElement) || !(sourceInput instanceof HTMLInputElement) || !amountInput.reportValidity()) return;
    saveField({ key, amount: amountInput.value, dataSource: sourceInput.value.trim(), gpcQualityRating: gridDraftMap.current.get(`${year.id}:${key}`)?.gpcQualityRating ?? savedFieldMap.current.get(`${year.id}:${key}`)?.gpcQualityRating ?? "", isAmountField: input.name.startsWith("amount:"), amountInput, sourceInput });
  }

  function handleCellUpdate({ rowId, columnId, value }: { rowId: string; columnId: string; value: string | number | null }): void {
    if (!canWrite || (columnId !== "AMOUNT" && columnId !== "SOURCE" && columnId !== "QUALITY")) return;
    if (columnId === "QUALITY" && value !== null && !isGhgQuality(value)) return;
    if (columnId === "AMOUNT" && value !== null && value !== "" && (!Number.isFinite(Number(value)) || Number(value) < 0)) return;
    if (columnId === "SOURCE" && String(value ?? "").trim().length > MUNICIPAL_PROPERTY_VALUE_CELL_POLICY.dataSourceMaxLength) {
      toast.error(t("register.property.saveError"));
      return;
    }
    const savedKey = `${year.id}:${rowId}`;
    const saved = gridDraftMap.current.get(savedKey) ?? savedFieldMap.current.get(savedKey);
    if (!saved) return;
    const amountElement = formRef.current?.elements.namedItem(`amount:${rowId}`);
    const sourceElement = formRef.current?.elements.namedItem(`source:${rowId}`);
    const amountInput = amountElement instanceof HTMLInputElement ? amountElement : undefined;
    const sourceInput = sourceElement instanceof HTMLInputElement ? sourceElement : undefined;
    const previous = { ...saved, amount: amountInput?.value ?? saved.amount, dataSource: sourceInput?.value.trim() ?? saved.dataSource };
    const field = columnId === "AMOUNT" ? "amount" : columnId === "SOURCE" ? "source" : "quality";
    const next = { ...previous, [SNAPSHOT_FIELD_MAP[field]]: value === null ? "" : String(value).trim() };
    gridDraftMap.current.set(savedKey, next);
    draftFieldSet.current.add(`${savedKey}:${field}`);
    setDraftMap(new Map(gridDraftMap.current));
    saveField({ key: rowId, ...next, isAmountField: columnId === "AMOUNT", isQualityField: columnId === "QUALITY", amountInput, sourceInput });
  }

  function handleBlur(event: FocusEvent<HTMLFormElement>): void {
    if (event.target instanceof HTMLInputElement)
      saveInput({ form: event.currentTarget, input: event.target });
  }

  function flushDrafts(form: HTMLFormElement): void {
    for (const cell of cellList) {
      const key = getConsumptionCellKey(cell);
      const savedKey = `${year.id}:${key}`;
      const field = draftFieldSet.current.has(`${savedKey}:amount`) ? "amount" : "source";
      if (!draftFieldSet.current.has(`${savedKey}:${field}`)) continue;
      const input = form.elements.namedItem(`${field}:${key}`);
      if (input instanceof HTMLInputElement) saveInput({ form, input });
    }
  }

  const flushDraftsRef = useRef(flushDrafts);
  useLayoutEffect(() => {
    flushDraftsRef.current = flushDrafts;
  });
  useLayoutEffect(() => {
    const form = formRef.current;
    return () => {
      if (form) flushDraftsRef.current(form);
    };
  }, []);

  return {
    formRef, handleChange, handleBlur, draftMap, handleCellUpdate,
    conflictDialog: conflictPreview ? createElement(MunicipalPropertyGhgConflictDialog, {
      preview: conflictPreview,
      onConfirm: () => settleConfirmation({ isConfirmed: true }),
      onCancel: () => settleConfirmation({ isConfirmed: false }),
    }) : null,
  };
}
